import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClimbingRoute } from '../models/climbing/ClimbingRoute';
import { updateClimbingRoute, getClimbingRoute } from '../services/firebase/climbingRoute';
import { getGym } from '../services/firebase/gym';
import { useAuth } from '../contexts/AuthContext';
import { AvailableColors } from '../models/climbing/RouteColors';
import { grades, GradeSystem, getGradesList } from '../models/climbing/Grade';
import NumberPicker from '../components/NumberPicker';
import '../styles/AddClimbingRoute.css';

const EditClimbingRoute: React.FC = () => {
  const { gymId, routeId } = useParams<{ gymId: string; routeId: string }>();
  const { currentUser, climberProfile } = useAuth();
  const navigate = useNavigate();

  // Check user's grade preferences
  const userBoulderGradeSystem = climberProfile?.boulderGradeSystem === 'YDS' ? GradeSystem.YDS : GradeSystem.FRENCH;
  const userRopeGradeSystem = climberProfile?.ropeGradeSystem === 'YDS' ? GradeSystem.YDS : GradeSystem.FRENCH;

  // State for form fields
  const [style, setStyle] = useState('');
  const [grade, setGrade] = useState<number | undefined>(undefined);
  const [gymStyles, setGymStyles] = useState<string[]>([]);
  const [color, setColor] = useState('');
  const [rope, setRope] = useState('');
  const [note, setNote] = useState('');
  const [comments, setComments] = useState('');
  const [status, setStatus] = useState('active');
  const [setterName, setSetterName] = useState('');
  const [originalRoute, setOriginalRoute] = useState<ClimbingRoute | null>(null);
  
  const [gymName, setGymName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch gym name and route data
  useEffect(() => {
    const fetchGymAndRoute = async () => {
      if (!gymId || !routeId) {
        setError('Invalid gym or route ID');
        setLoading(false);
        return;
      }
      
      try {
        // Fetch gym details
        const gym = await getGym(gymId);
        if (!gym) {
          setError('Gym not found');
          setLoading(false);
          return;
        }
        
        setGymName(gym.name || 'Unnamed Gym');
        
        // Fetch gym styles
        if (gym.styles && Array.isArray(gym.styles)) {
          setGymStyles(gym.styles);
        }
        
        // Fetch route details
        const route = await getClimbingRoute(gymId, routeId);
        if (!route) {
          setError('Route not found');
          setLoading(false);
          return;
        }
        
        setOriginalRoute(route);
        
        // Set form values from route data
        setStyle(route.style || '');
        setGrade(route.grade);
        setColor(route.color || '');
        setRope(route.rope || '');
        setNote(route.note || '');
        setComments(route.comments || '');
        setStatus(route.status || 'active');
        setSetterName(route.setterName || '');
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(`Failed to load data: ${err.message}`);
        setLoading(false);
      }
    };
    
    fetchGymAndRoute();
  }, [gymId, routeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gymId || !routeId) {
      return setError('Invalid gym or route ID');
    }
    
    if (!originalRoute) {
      return setError('Original route data not found');
    }
    
    // Collect validation errors
    const validationErrors: string[] = [];
    
    // Determine the route style
    let routeStyle = style.trim();
    
    // Auto-select style if gym only has one climbing style and no style is selected
    if (!routeStyle && gymStyles.length === 1) {
      if (gymStyles[0] === 'Rope climbing') {
        routeStyle = 'Lead';
      } else if (gymStyles[0] === 'Bouldering') {
        routeStyle = 'Boulder';
      }
    }
    
    if (!routeStyle) {
      validationErrors.push('Style is required');
    }
    
    if (grade === undefined) {
      validationErrors.push('Grade is required');
    }
    
    if (!color.trim()) {
      validationErrors.push('Color is required');
    }

    // Show validation dialog if there are errors
    if (validationErrors.length > 0) {
      alert('Please fill in the following required fields:\n\n' + validationErrors.join('\n'));
      return;
    }

    try {
      setSubmitLoading(true);
      setError('');
      
      // Update route properties but keep original ID and setter info
      const updatedRoute = new ClimbingRoute(originalRoute.id, {
        style: routeStyle,
        grade: grade,
        color: color,
        rope: rope.trim() || undefined,
        setterId: originalRoute.setterId, // Keep original setter
        setterName: setterName.trim() || originalRoute.setterName, // Allow editing setter name
        note: note.trim() || undefined,
        comments: comments.trim() || undefined,
        date: originalRoute.date, // Keep original date
        status: status.trim() || 'active',
        points: 100 // Default to 100 bananas
      });
      
      // Update in Firestore
      await updateClimbingRoute(gymId, updatedRoute);
      
      // Navigate back to route detail
      navigate(`/gyms/${gymId}/routes/${routeId}`);
    } catch (err: any) {
      console.error('Error updating climbing route:', err);
      setError(`Failed to update climbing route: ${err.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="add-climbing-route-container">
        <div className="loading-indicator">Loading route data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="add-climbing-route-container">
        <div className="error-message">{error}</div>
        <button 
          className="cancel-button"
          onClick={() => navigate(`/gyms/${gymId}/routes/${routeId}`)}
          style={{ margin: '20px auto', display: 'block' }}
        >
          Back to Route
        </button>
      </div>
    );
  }

  return (
    <div className="add-climbing-route-container">
      <div className="add-route-header">
        <h1>Edit Climbing Route</h1>
        <p className="gym-name-indicator">at {gymName}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form className="add-route-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="setterName">Setter Name</label>
          <input
            type="text"
            id="setterName"
            value={setterName}
            onChange={(e) => setSetterName(e.target.value)}
            placeholder="Route setter name"
          />
        </div>

        {/* Show style selection as chips - only show styles supported by the gym */}
        {(gymStyles.includes('Bouldering') || gymStyles.includes('Rope climbing')) && (
          <div className="form-group">
            <label>Style *</label>
            <div className="style-chips">
              {gymStyles.includes('Bouldering') && (
                <div
                  className={`style-chip ${style === 'Boulder' ? 'selected' : ''}`}
                  onClick={() => setStyle('Boulder')}
                >
                  Boulder
                </div>
              )}
              {gymStyles.includes('Rope climbing') && (
                <div
                  className={`style-chip ${style === 'Lead' ? 'selected' : ''}`}
                  onClick={() => setStyle('Lead')}
                >
                  Rope
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="grade">Grade *</label>
          <div className="grade-selection">
            {/* Use ropeGradeSystem or boulderGradeSystem based on the selected style */}
            {(currentUser?.uid && climberProfile) ? (
              // If user is logged in and has a profile, show grades based on their preferences
              style === 'Boulder' ? (
                // For bouldering, use the user's boulder grade system preference
                getGradesList(userBoulderGradeSystem).map(g => (
                  <div
                    key={g.gradeNr}
                    className={`grade-option ${grade === g.gradeNr ? 'selected' : ''}`}
                    onClick={() => setGrade(g.gradeNr)}
                    title={g.french ? g.french : g.yds}
                  >
                    {userBoulderGradeSystem === GradeSystem.YDS ? g.yds : g.french}
                  </div>
                ))
              ) : (
                // For rope climbing, use the user's rope grade system preference
                getGradesList(userRopeGradeSystem).map(g => (
                  <div
                    key={g.gradeNr}
                    className={`grade-option ${grade === g.gradeNr ? 'selected' : ''}`}
                    onClick={() => setGrade(g.gradeNr)}
                    title={g.french ? g.french : g.yds}
                  >
                    {userRopeGradeSystem === GradeSystem.YDS ? g.yds : g.french}
                  </div>
                ))
              )
            ) : (
              // Default to French grades if no user preference
              getGradesList(GradeSystem.FRENCH).map(g => (
                <div
                  key={g.gradeNr}
                  className={`grade-option ${grade === g.gradeNr ? 'selected' : ''}`}
                  onClick={() => setGrade(g.gradeNr)}
                  title={g.french}
                >
                  {g.french}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="color">Color *</label>
          <div className="color-selection">
            {AvailableColors.map(colorOption => (
              <div 
                key={colorOption.value}
                className={`color-option ${color === colorOption.value ? 'selected' : ''} ${colorOption.value === 'white' ? 'white-color' : ''}`}
                style={{ backgroundColor: colorOption.hexCode }}
                onClick={() => setColor(colorOption.value)}
                title={colorOption.name}
              ></div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="removed">Removed</option>
          </select>
        </div>

        <div className="form-group rope-picker-group">
          <label htmlFor="rope">Rope Number</label>
          <NumberPicker
            value={rope}
            onChange={setRope}
            minValue={1}
            maxValue={99}
            placeholder="Select rope number"
            initialOpen={true}
          />
        </div>

        <div className="form-group" style={{ clear: 'both', width: '100%' }}>
          <label htmlFor="comments">Description</label>
          <textarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Description of the climbing route"
            rows={3}
          />
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate(`/gyms/${gymId}/routes/${routeId}`)}
            style={{ flex: 1, maxWidth: '200px', height: '45px' }}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="save-button"
            disabled={submitLoading}
            style={{ flex: 1, maxWidth: '200px', height: '45px' }}
          >
            {submitLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditClimbingRoute;
